import { PayoutFilters } from '../../types/payouts';

export const queryKeys = {
  notifications: {
    byUser: (userId: string) => ['notifications', userId] as const,
  },
  links: {
    byUser: (userId: string, filters?: Record<string, unknown>) =>
      ['links', userId, filters] as const,
    isAvailable: (userId: string, linkId: string, shortUrl: string) =>
      ['links', 'isAvailable', userId, linkId, shortUrl] as const,
    youtubeStats: (userId: string, linkId: string, interval: string) =>
      ['links', 'youtubeStats', userId, linkId, interval] as const,
  },
  user: {
    profile: (userId: string) => ['user', 'profile', userId] as const,
    brandUsers: (
      userId: string,
      brandId: string,
      page: number,
      limit: number,
      search?: string,
    ) => ['user', 'brandUsers', userId, brandId, page, limit, search] as const,
    brand: (userId: string, brandId: string) =>
      ['user', 'brand', userId, brandId] as const,
    deposits: (brandId: string, page: number, limit: number) =>
      ['deposits', brandId, page, limit] as const,
    totals: (brandId: string) => ['totals', brandId] as const,
    subscription: (userId: string) => ['user', 'subscription', userId] as const,
    profileCompletion: (userId: string) =>
      ['user', 'profileCompletion', userId] as const,
    adminProfile: (userId: string) => ['user', 'adminProfile', userId] as const,
  },
  analytics: {
    dashboard: (interval: string) =>
      ['analytics', 'dashboard', interval] as const,
    full: (interval: string) => ['analytics', 'full', interval] as const,
    general: (interval: string) => ['analytics', 'general', interval] as const,
    byLink: (linkId: string, interval: string) =>
      ['analytics', 'link', linkId, interval] as const,
    byUser: (userId: string, interval: string) =>
      ['analytics', 'user', userId, interval] as const,
    byUserLink: (userId: string, linkId: string, interval: string) =>
      ['analytics', 'userLink', userId, linkId, interval] as const,
    topLinks: (filter?: string) =>
      ['analytics', 'admin', 'topLinks', filter] as const,
    topUsers: (filter?: string) =>
      ['analytics', 'admin', 'topUsers', filter] as const,
    topRegions: (filter?: string) =>
      ['analytics', 'admin', 'topRegions', filter] as const,
    topCountries: (filter?: string) =>
      ['analytics', 'admin', 'topCountries', filter] as const,
    topDevices: (filter?: string) =>
      ['analytics', 'admin', 'topDevices', filter] as const,
    topPages: () => ['analytics', 'admin', 'topPages'] as const,
    topReferrers: () => ['analytics', 'admin', 'topReferrers'] as const,
    topBrowsers: () => ['analytics', 'admin', 'topBrowsers'] as const,
    topPlatforms: (filter?: string) =>
      ['analytics', 'admin', 'topPlatforms', filter] as const,
    totalClicks: (filter?: string) =>
      ['analytics', 'admin', 'totalClicks', filter] as const,
    newClicks: (filter?: string) =>
      ['analytics', 'admin', 'newClicks', filter] as const,
    totalUsers: (filter?: string) =>
      ['analytics', 'admin', 'totalUsers', filter] as const,
    newUsers: (filter?: string) =>
      ['analytics', 'admin', 'newUsers', filter] as const,
    totalLinks: (filter?: string) =>
      ['analytics', 'admin', 'totalLinks', filter] as const,
    newLinks: (filter?: string) =>
      ['analytics', 'admin', 'newLinks', filter] as const,
    totalAnnualSubs: (filter?: string) =>
      ['analytics', 'admin', 'totalAnnualSubs', filter] as const,
    totalMonthlySubs: (filter?: string) =>
      ['analytics', 'admin', 'totalMonthlySubs', filter] as const,
    userStatistics: (filter?: string) =>
      ['analytics', 'admin', 'userStatistics', filter] as const,
    performanceGraphs: (filter?: string) =>
      ['analytics', 'admin', 'performanceGraphs', filter] as const,
    usersEvolution: (filter: string) =>
      ['analytics', 'admin', 'usersEvolution', filter] as const,
    linksEvolution: (filter: string) =>
      ['analytics', 'admin', 'linksEvolution', filter] as const,
    userCreationOverTime: (filter: string) =>
      ['analytics', 'admin', 'userCreationOverTime', filter] as const,
    linkPerformanceOverTime: (filter: string) =>
      ['analytics', 'admin', 'linkPerformanceOverTime', filter] as const,
    campaignAnalysis: (
      brandId: string,
      filter: string,
      startDate?: string,
      endDate?: string,
    ) =>
      [
        'analytics',
        'brand',
        'campaignAnalysis',
        brandId,
        filter,
        startDate,
        endDate,
      ] as const,
    totalSellChart: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'totalSell', brandId, filter] as const,
    earnedMediaValueChart: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'earnedMediaValue', brandId, filter] as const,
    contentAnalysis: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'contentAnalysis', brandId, filter] as const,
    engagementAnalysis: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'engagementAnalysis', brandId, filter] as const,
    creatorAnalysis: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'creatorAnalysis', brandId, filter] as const,
    creatorUserDetails: (brandId: string, userId: string, campaignId: string) =>
      [
        'analytics',
        'brand',
        'creatorUserDetails',
        brandId,
        userId,
        campaignId,
      ] as const,
    creatorAllSubmissions: (brandId: string, userId: string) =>
      ['analytics', 'brand', 'creatorAllSubmissions', brandId, userId] as const,
    campaignChart: (campaignId: string, filter: string) =>
      ['analytics', 'campaign', 'chart', campaignId, filter] as const,
    campaignKpis: (campaignId: string) =>
      ['analytics', 'campaign', 'kpis', campaignId] as const,
    campaignTopPosts: (campaignId: string) =>
      ['analytics', 'campaign', 'topPosts', campaignId] as const,
    campaignCreatorsReach: (campaignId: string) =>
      ['analytics', 'campaign', 'creatorsReach', campaignId] as const,
    roasAnalysis: (brandId: string, filter: string) =>
      ['analytics', 'brand', 'roasAnalysis', brandId, filter] as const,
    campaignCreatorList: (campaignId: string) =>
      ['analytics', 'campaign', 'creatorList', campaignId] as const,
    campaignRoasChart: (campaignId: string, filter: string) =>
      ['analytics', 'campaign', 'roasChart', campaignId, filter] as const,
    brandSentiment: (
      brandId: string,
      filter?: string,
      startDate?: string,
      endDate?: string,
    ) =>
      [
        'analytics',
        'brand',
        'sentiment',
        brandId,
        filter,
        startDate,
        endDate,
      ] as const,
  },
  campaigns: {
    byStatus: (status: string, page: number, limit: number) =>
      ['campaigns', 'byStatus', status, page, limit] as const,
    byStatusInfinite: (status: string) =>
      ['campaigns', 'infinite', status] as const,
    byId: (campaignId: string) => ['campaigns', campaignId] as const,
    stats: (brandId: string) => ['campaigns', 'stats', brandId] as const,
  },
  brands: {
    list: (page: number, limit: number, searchTerm?: string) =>
      ['brands', 'list', page, limit, searchTerm] as const,
    byId: (brandId: string) => ['brands', brandId] as const,
    memberList: (
      brandId: string,
      page: number,
      limit: number,
      search?: string,
    ) => ['brands', 'memberList', brandId, page, limit, search] as const,
    deposit: (brandId: string, page: number, limit: number) =>
      ['brands', 'deposit', brandId, page, limit] as const,
  },
  admin: {
    users: (filters: Record<string, unknown>) =>
      ['admin', 'users', filters] as const,
    userProfile: (userId: string) => ['admin', 'userProfile', userId] as const,
    flaggedSubmissions: (filters: Record<string, unknown>) =>
      ['admin', 'flaggedSubmissions', filters] as const,
    adminAnalytics: () => ['admin', 'analytics'] as const,
    allUsersAndLinks: () => ['admin', 'allUsersAndLinks'] as const,
    allUsersAndLinksPaged: (
      filter: string,
      page: number,
      limit: number,
      sortField: string,
      sortOrder: string,
    ) =>
      [
        'admin',
        'allUsersAndLinksPaged',
        filter,
        page,
        limit,
        sortField,
        sortOrder,
      ] as const,
    userById: (userId: string) => ['admin', 'userById', userId] as const,
    payouts: (filters: PayoutFilters) => ['admin', 'payouts', filters] as const,
    payoutDetail: (paymentRef: string | null | undefined) =>
      ['admin', 'payoutDetail', paymentRef] as const,
    companyBalance: () => ['admin', 'companyBalance'] as const,
  },
  subscribers: {
    brands: () => ['subscribers', 'brands'] as const,
    creators: () => ['subscribers', 'creators'] as const,
    newsletter: () => ['subscribers', 'newsletter'] as const,
  },
  location: {
    search: (keyword: string, areaType?: string) =>
      ['location', keyword, areaType] as const,
  },
  wishlist: {
    creators: (page: number, limit: number, search?: string) =>
      ['wishlist', 'creators', page, limit, search] as const,
  },
  instagram: {
    profile: (userId: string) => ['instagram', 'profile', userId] as const,
  },
} as const;
